import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import { searchOutline, timeOutline } from "ionicons/icons";
import { FC, ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import styled from "styled-components";
import { cardSurface } from "../../components/atoms/Card";
import Icon from "../../components/atoms/Icon";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { ArticleCardType, getStudyArticles } from "../../services/study.service";

/**
 * Resources page — the researcher-managed article library. Every card is DERIVED from
 * an ARTICLE content's own blocks (hero → cover + title, first paragraph → teaser), so
 * the whole page is authored in Diapason with zero developer involvement per article.
 */
const DisplayResources: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const { study_slug } = useParams<{ study_slug: string }>();
  const { study } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [articles, setArticles] = useState<ArticleCardType[] | null>(null);
  const [isError, setIsError] = useState(false);
  const [query, setQuery] = useState("");

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      // Explicit white like every tab page — the shared layout otherwise keeps the
      // previous tab's color (e.g. the island map's sea).
      backgroundColor: "white",
      title: "resources_tab_label",
      welcomePage: false,
      // Defensive: coming back from an article (which shows a back arrow).
      headerLeft: undefined, // project default
    });
  });

  useEffect(() => {
    let active = true;
    if (!study?.id) return undefined;
    getStudyArticles({ studyId: study.id, lang: language })
      .then(({ data }) => {
        if (active) setArticles(data);
      })
      .catch(() => {
        if (active) setIsError(true);
      });
    return () => {
      active = false;
    };
  }, [study?.id, language]);

  const openArticle = (id: number): void => {
    ionRouter.push(`/${study_slug}/tabs/resources/article/${id}`, "forward", "push");
  };

  const filtered = (articles ?? []).filter((article) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return (
      article.title.toLowerCase().includes(needle) ||
      article.description.toLowerCase().includes(needle)
    );
  });

  return (
    <ResonanceLayout>
      <Page>
        {isError ? (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : articles === null ? (
          <ResonanceLoader />
        ) : (
          <>
            <SearchRow>
              <Icon icon={searchOutline} />
              <SearchInput
                type="search"
                value={query}
                placeholder={t("resources_search_placeholder")}
                onChange={(e) => setQuery(e.target.value)}
              />
            </SearchRow>

            {filtered.length === 0 ? (
              <Empty>{t("resources_empty")}</Empty>
            ) : (
              <CardsList>
                {filtered.map((article) => (
                  <ArticleCard
                    key={article.id}
                    type="button"
                    onClick={() => openArticle(article.id)}
                  >
                    {article.image && <Cover src={article.image} alt="" loading="lazy" />}
                    <CardBody>
                      <CardTitle>{article.title}</CardTitle>
                      {article.description && (
                        <CardDescription>{article.description}</CardDescription>
                      )}
                      {article.avg_amount_time_to_read > 0 && (
                        <ReadTime>
                          <Icon icon={timeOutline} size="small" />
                          {article.avg_amount_time_to_read} {t("questionnaire_minutes")}
                        </ReadTime>
                      )}
                    </CardBody>
                  </ArticleCard>
                ))}
              </CardsList>
            )}
          </>
        )}
      </Page>
    </ResonanceLayout>
  );
};

const Page = styled.div`
  padding: 16px 20px 90px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SearchRow = styled.div`
  ${cardSurface}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--app-color-surface-tint, white);
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--app-color-text, #041723);

  &::placeholder {
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.45);
  }
`;

/* Responsive grid: one column on phones, as many ~300px columns as fit on wider
   screens — a lone article renders as one modest card, not a full-width billboard. */
const CardsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  align-items: start;
`;

const ArticleCard = styled.button`
  ${cardSurface}
  padding: 0;
  text-align: left;
  overflow: hidden;
  background: var(--app-color-surface-tint, white);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.75;
  }
`;

const Cover = styled.img`
  width: 100%;
  aspect-ratio: 16 / 8;
  object-fit: cover;
  display: block;
`;

const CardBody = styled.div`
  padding: 12px 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--app-color-text, #041723);
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.7);
`;

const ReadTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--app-color-accent, #2db6b4);
`;

const Empty = styled.p`
  text-align: center;
  padding: 40px 20px;
  font-size: 15px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
`;

export default DisplayResources;
